import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ExternalLink,
  LoaderCircle,
  Music2,
  RefreshCw,
  Send,
  Settings2,
  Share2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import type {
  JobResult,
  SocialAccounts,
  SocialConnection,
  SocialDraft,
  SocialProvider,
  SocialPublish,
  TikTokCreatorInfo,
} from "./types";

type CoverCandidate = {
  timestampMs: number;
  previewUrl: string;
  score: number;
};

type Props = {
  result: JobResult;
  accounts: SocialAccounts | null;
  onManageAccounts: () => void;
  announce: (message: string) => void;
};

function timeLabel(timestampMs: number): string {
  return `${(timestampMs / 1000).toFixed(1)}초`;
}

function waitFor(video: HTMLVideoElement, event: "loadedmetadata" | "seeked"): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("커버 프레임을 읽는 데 시간이 오래 걸립니다."));
    }, 12_000);
    const cleanup = () => {
      window.clearTimeout(timeout);
      video.removeEventListener(event, resolved);
      video.removeEventListener("error", failed);
    };
    const resolved = () => {
      cleanup();
      resolve();
    };
    const failed = () => {
      cleanup();
      reject(new Error("영상에서 커버 프레임을 읽지 못했습니다."));
    };
    video.addEventListener(event, resolved, { once: true });
    video.addEventListener("error", failed, { once: true });
  });
}

function visualScore(data: ImageData): number {
  const { width, height } = data;
  const luminance = new Float32Array(width * height);
  let sum = 0;
  let sumSquares = 0;
  for (let index = 0; index < width * height; index += 1) {
    const offset = index * 4;
    const value = data.data[offset] * 0.2126 + data.data[offset + 1] * 0.7152 + data.data[offset + 2] * 0.0722;
    luminance[index] = value;
    sum += value;
    sumSquares += value * value;
  }
  const mean = sum / luminance.length;
  const contrast = Math.sqrt(Math.max(0, sumSquares / luminance.length - mean * mean));
  let edge = 0;
  let centerEdge = 0;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const index = y * width + x;
      const laplacian = Math.abs(
        luminance[index - 1] + luminance[index + 1] + luminance[index - width] + luminance[index + width]
        - 4 * luminance[index],
      );
      edge += laplacian;
      if (x > width * 0.18 && x < width * 0.82 && y > height * 0.14 && y < height * 0.82) centerEdge += laplacian;
    }
  }
  const exposurePenalty = Math.abs(mean - 132) * 0.34;
  return edge / luminance.length + centerEdge / luminance.length * 0.55 + contrast * 0.35 - exposurePenalty;
}

export async function recommendCoverFrames(videoUrl: string): Promise<CoverCandidate[]> {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = videoUrl;
  if (video.readyState < 1) await waitFor(video, "loadedmetadata");
  const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 10;
  const sampleTimes = Array.from({ length: 9 }, (_, index) => duration * (0.1 + index * 0.1));
  const scoringCanvas = document.createElement("canvas");
  scoringCanvas.width = 72;
  scoringCanvas.height = 128;
  const scoring = scoringCanvas.getContext("2d", { willReadFrequently: true });
  const previewCanvas = document.createElement("canvas");
  previewCanvas.width = 180;
  previewCanvas.height = 320;
  const preview = previewCanvas.getContext("2d");
  if (!scoring || !preview) throw new Error("브라우저가 커버 분석을 지원하지 않습니다.");
  const candidates: CoverCandidate[] = [];
  for (const time of sampleTimes) {
    video.currentTime = Math.min(Math.max(0, time), Math.max(0, duration - 0.05));
    await waitFor(video, "seeked");
    scoring.drawImage(video, 0, 0, scoringCanvas.width, scoringCanvas.height);
    preview.drawImage(video, 0, 0, previewCanvas.width, previewCanvas.height);
    candidates.push({
      timestampMs: Math.round(video.currentTime * 1000),
      previewUrl: previewCanvas.toDataURL("image/jpeg", 0.78),
      score: visualScore(scoring.getImageData(0, 0, scoringCanvas.width, scoringCanvas.height)),
    });
  }
  video.removeAttribute("src");
  video.load();
  const selected: CoverCandidate[] = [];
  for (const candidate of candidates.sort((left, right) => right.score - left.score)) {
    if (selected.every((item) => Math.abs(item.timestampMs - candidate.timestampMs) >= Math.min(900, duration * 80))) {
      selected.push(candidate);
    }
    if (selected.length === 3) break;
  }
  return selected.sort((left, right) => right.score - left.score);
}

function hashtagInput(tags: string[]): string {
  return tags.join(" ");
}

function parseHashtags(value: string): string[] {
  return [...new Set(value.split(/\s+/).map((tag) => tag.trim()).filter(Boolean))];
}

function accountLabel(account: SocialConnection): string {
  return account.username ? `@${account.username}` : account.display_name || "연결된 계정";
}

function providerLabel(provider: SocialProvider): string {
  return provider === "instagram" ? "Instagram" : "TikTok";
}

function privacyLabel(value: string): string {
  if (value === "PUBLIC_TO_EVERYONE") return "전체 공개";
  if (value === "MUTUAL_FOLLOW_FRIENDS") return "맞팔 친구";
  if (value === "FOLLOWER_OF_CREATOR") return "팔로워";
  if (value === "SELF_ONLY") return "나만 보기";
  return value;
}

function PublishHistory({ publishes }: { publishes: SocialPublish[] }) {
  if (!publishes.length) return null;
  return (
    <section className="social-publish-history" aria-label="게시 기록">
      <strong>게시 기록</strong>
      <div>
        {publishes.map((publish) => (
          <div key={publish.id} className={`social-publish-row ${publish.status}`}>
            {publish.status === "processing" ? <LoaderCircle className="spin" size={15} />
              : publish.status === "failed" ? <AlertTriangle size={15} /> : <CheckCircle2 size={15} />}
            <span>
              <strong>{providerLabel(publish.provider)} · {publish.username ? `@${publish.username}` : publish.display_name}</strong>
              <small>{publish.error || publish.stage || (publish.status === "published" ? "게시 완료" : "TikTok 앱에서 편집 가능")}</small>
            </span>
            {publish.permalink && <a href={publish.permalink} target="_blank" rel="noreferrer">게시물 열기<ExternalLink size={13} /></a>}
          </div>
        ))}
      </div>
    </section>
  );
}

export function SocialPublisher({ result, accounts, onManageAccounts, announce }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<SocialDraft | null>(null);
  const [covers, setCovers] = useState<CoverCandidate[]>([]);
  const [selectedCoverMs, setSelectedCoverMs] = useState(0);
  const [instagramCaption, setInstagramCaption] = useState("");
  const [instagramHashtags, setInstagramHashtags] = useState("");
  const [instagramAudioName, setInstagramAudioName] = useState("");
  const [tiktokCaption, setTiktokCaption] = useState("");
  const [tiktokHashtags, setTiktokHashtags] = useState("");
  const [selectedInstagram, setSelectedInstagram] = useState("");
  const [selectedTikTok, setSelectedTikTok] = useState("");
  const [instagramEnabled, setInstagramEnabled] = useState(true);
  const [tiktokEnabled, setTikTokEnabled] = useState(true);
  const [tiktokMode, setTikTokMode] = useState<"direct" | "draft">("direct");
  const [creator, setCreator] = useState<TikTokCreatorInfo | null>(null);
  const [privacy, setPrivacy] = useState("");
  const [allowComment, setAllowComment] = useState(false);
  const [allowDuet, setAllowDuet] = useState(false);
  const [allowStitch, setAllowStitch] = useState(false);
  const [commercial, setCommercial] = useState(false);
  const [ownBrand, setOwnBrand] = useState(false);
  const [brandedContent, setBrandedContent] = useState(false);
  const [musicConfirmed, setMusicConfirmed] = useState(false);
  const [mediaVariant, setMediaVariant] = useState<"original" | "motion_fix">("original");
  const [publishes, setPublishes] = useState<SocialPublish[]>([]);
  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [coverLoading, setCoverLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [initialized, setInitialized] = useState(false);

  const instagramAccounts = useMemo(
    () => accounts?.connections.filter((account) => account.provider === "instagram") || [],
    [accounts],
  );
  const tiktokAccounts = useMemo(
    () => accounts?.connections.filter((account) => account.provider === "tiktok") || [],
    [accounts],
  );
  const tiktokAccount = tiktokAccounts.find((account) => account.id === selectedTikTok) || null;
  const canDirectTikTok = Boolean(tiktokAccount?.scopes.includes("video.publish"));
  const canDraftTikTok = Boolean(tiktokAccount?.scopes.includes("video.upload"));

  const loadPublishes = useCallback(async () => {
    try {
      const response = await api.socialPublishes(result.id);
      setPublishes(response.publishes);
    } catch {
      // Publishing remains usable even if an old history row cannot be read.
    }
  }, [result.id]);

  const generateDraft = useCallback(async () => {
    setDraftLoading(true);
    setError("");
    try {
      const response = await api.socialDraft(result.id);
      setDraft(response);
      setInstagramCaption(response.instagram.caption);
      setInstagramHashtags(hashtagInput(response.instagram.hashtags));
      setInstagramAudioName(response.instagram.audio_name);
      setTiktokCaption(response.tiktok.caption);
      setTiktokHashtags(hashtagInput(response.tiktok.hashtags));
      setTikTokMode(response.music.recommendation === "tiktok_edit" ? "draft" : "direct");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "게시 문구를 만들지 못했습니다.");
    } finally {
      setDraftLoading(false);
    }
  }, [result.id]);

  const generateCovers = useCallback(async () => {
    setCoverLoading(true);
    try {
      const response = await recommendCoverFrames(result.video_url);
      setCovers(response);
      setSelectedCoverMs(response[0]?.timestampMs || 0);
    } catch (reason) {
      setCovers([]);
      setSelectedCoverMs(0);
      setError((current) => current || (reason instanceof Error ? reason.message : "커버 후보를 만들지 못했습니다."));
    } finally {
      setCoverLoading(false);
    }
  }, [result.video_url]);

  useEffect(() => {
    if (!open || initialized) return;
    setInitialized(true);
    setLoading(true);
    Promise.all([loadPublishes(), generateDraft(), generateCovers()])
      .catch((reason) => setError(reason instanceof Error ? reason.message : "게시 준비 정보를 불러오지 못했습니다."))
      .finally(() => setLoading(false));
  }, [generateCovers, generateDraft, initialized, loadPublishes, open]);

  useEffect(() => {
    const instagramIds = new Set(instagramAccounts.map((account) => account.id));
    const tiktokIds = new Set(tiktokAccounts.map((account) => account.id));
    setSelectedInstagram((current) => instagramIds.has(current) ? current : instagramAccounts[0]?.id || "");
    setSelectedTikTok((current) => tiktokIds.has(current) ? current : tiktokAccounts[0]?.id || "");
  }, [instagramAccounts, tiktokAccounts]);

  useEffect(() => {
    if (!open || !selectedTikTok || !canDirectTikTok) {
      setCreator(null);
      return;
    }
    let cancelled = false;
    api.tiktokCreatorInfo(selectedTikTok).then((response) => {
      if (!cancelled) setCreator(response);
    }).catch((reason) => {
      if (!cancelled) setError(reason instanceof Error ? reason.message : "TikTok 게시 권한을 확인하지 못했습니다.");
    });
    return () => { cancelled = true; };
  }, [canDirectTikTok, open, selectedTikTok]);

  useEffect(() => {
    if (!canDirectTikTok && canDraftTikTok) setTikTokMode("draft");
    if (!canDraftTikTok && canDirectTikTok) setTikTokMode("direct");
  }, [canDirectTikTok, canDraftTikTok]);

  useEffect(() => {
    if (!open || !publishes.some((publish) => publish.status === "processing")) return;
    const timer = window.setInterval(() => void loadPublishes(), 4000);
    return () => window.clearInterval(timer);
  }, [loadPublishes, open, publishes]);

  const invalidCommercial = commercial && !ownBrand && !brandedContent;
  const tikTokDirectReady = tiktokMode === "draft" || Boolean(creator && privacy);
  const selectedTargets = Number(instagramEnabled && selectedInstagram) + Number(tiktokEnabled && selectedTikTok);
  const canPublish = Boolean(
    draft && selectedTargets > 0 && !publishing && !invalidCommercial
    && (!tiktokEnabled || !selectedTikTok || (musicConfirmed && tikTokDirectReady)),
  );

  async function publish() {
    if (!canPublish) return;
    setPublishing(true);
    setError("");
    const requests: Promise<SocialPublish>[] = [];
    if (instagramEnabled && selectedInstagram) {
      requests.push(api.publishSocial(result.id, {
        client_request_id: crypto.randomUUID(),
        connection_id: selectedInstagram,
        provider: "instagram",
        mode: "direct",
        caption: instagramCaption,
        hashtags: parseHashtags(instagramHashtags),
        cover_timestamp_ms: selectedCoverMs,
        media_variant: mediaVariant,
        audio_name: instagramAudioName,
      }));
    }
    if (tiktokEnabled && selectedTikTok) {
      if (tiktokMode === "draft" && navigator.clipboard) {
        void navigator.clipboard.writeText(`${tiktokCaption}\n\n${tiktokHashtags}`.trim()).catch(() => undefined);
      }
      requests.push(api.publishSocial(result.id, {
        client_request_id: crypto.randomUUID(),
        connection_id: selectedTikTok,
        provider: "tiktok",
        mode: tiktokMode,
        caption: tiktokCaption,
        hashtags: parseHashtags(tiktokHashtags),
        cover_timestamp_ms: selectedCoverMs,
        media_variant: mediaVariant,
        privacy_level: privacy,
        allow_comment: allowComment,
        allow_duet: allowDuet,
        allow_stitch: allowStitch,
        own_brand: commercial && ownBrand,
        branded_content: commercial && brandedContent,
        music_usage_confirmed: musicConfirmed,
      }));
    }
    try {
      const outcomes = await Promise.allSettled(requests);
      const rejected = outcomes.filter((outcome) => outcome.status === "rejected") as PromiseRejectedResult[];
      await loadPublishes();
      if (rejected.length) {
        setError(rejected.map((outcome) => outcome.reason instanceof Error ? outcome.reason.message : "게시 요청 실패").join(" · "));
      } else {
        announce(tiktokMode === "draft" && tiktokEnabled
          ? "게시 요청을 보냈습니다. TikTok 문구는 복사했으며 앱에서 음원을 골라 마무리하세요."
          : "게시 요청을 보냈습니다. 플랫폼 처리 상태를 자동으로 확인합니다.");
      }
    } finally {
      setPublishing(false);
    }
  }

  return (
    <section className={`social-publisher ${open ? "open" : ""}`}>
      {!open ? (
        <button className="button primary compact" onClick={() => setOpen(true)}>
          <Share2 size={15} />게시하기
        </button>
      ) : (
        <>
          <header className="social-publisher-heading">
            <div><Share2 size={17} /><span><strong>Instagram · TikTok 게시</strong><small>문구와 커버를 확인한 뒤 한 번에 전송합니다.</small></span></div>
            <button className="icon-button" onClick={() => setOpen(false)} aria-label="게시 패널 닫기"><X size={17} /></button>
          </header>

          {loading && !draft ? <p className="social-loading"><LoaderCircle className="spin" size={16} />게시 패키지 준비 중</p> : (
            <>
              <div className="social-account-strip">
                <div className="social-account-provider">
                  <span className="social-platform-icon"><Settings2 size={16} /></span>
                  <span>
                    <strong>미리 연결한 게시 계정</strong>
                    <small>Instagram {instagramAccounts.length}개 · TikTok {tiktokAccounts.length}개</small>
                  </span>
                  <button className="button subtle compact" onClick={onManageAccounts}>계정 추가·관리</button>
                </div>
              </div>

              {draft && (
                <div className="social-hook">
                  <SparkleMark />
                  <span><strong>{draft.hook}</strong><small>추천 커버 문구 · {draft.cover_title || "문구 없이 사용"}</small></span>
                  <button className="button subtle compact" onClick={() => void generateDraft()} disabled={draftLoading}>
                    {draftLoading ? <LoaderCircle className="spin" size={14} /> : <RefreshCw size={14} />}다시 추천
                  </button>
                </div>
              )}

              <div className="social-compose-grid">
                <fieldset className="social-platform-compose" disabled={!instagramAccounts.length}>
                  <legend><label><input type="checkbox" checked={instagramEnabled} onChange={(event) => setInstagramEnabled(event.target.checked)} />Instagram Reels</label></legend>
                  <label>게시 계정<select value={selectedInstagram} onChange={(event) => setSelectedInstagram(event.target.value)}><option value="">계정 선택</option>{instagramAccounts.map((account) => <option value={account.id} key={account.id}>{accountLabel(account)}</option>)}</select></label>
                  <label>본문<textarea rows={5} value={instagramCaption} onChange={(event) => setInstagramCaption(event.target.value)} /></label>
                  <label>해시태그<input value={instagramHashtags} onChange={(event) => setInstagramHashtags(event.target.value)} /></label>
                  <label>원본 오디오 이름<input maxLength={30} value={instagramAudioName} onChange={(event) => setInstagramAudioName(event.target.value)} /></label>
                </fieldset>

                <fieldset className="social-platform-compose" disabled={!tiktokAccounts.length}>
                  <legend><label><input type="checkbox" checked={tiktokEnabled} onChange={(event) => setTikTokEnabled(event.target.checked)} />TikTok</label></legend>
                  <label>게시 계정<select value={selectedTikTok} onChange={(event) => { setSelectedTikTok(event.target.value); setPrivacy(""); }}><option value="">계정 선택</option>{tiktokAccounts.map((account) => <option value={account.id} key={account.id}>{accountLabel(account)}</option>)}</select></label>
                  <label>본문<textarea rows={5} value={tiktokCaption} onChange={(event) => setTiktokCaption(event.target.value)} /></label>
                  <label>해시태그<input value={tiktokHashtags} onChange={(event) => setTiktokHashtags(event.target.value)} /></label>
                  <div className="tiktok-mode" role="radiogroup" aria-label="TikTok 게시 방식">
                    <label><input type="radio" name={`tiktok-mode-${result.id}`} checked={tiktokMode === "direct"} onChange={() => setTikTokMode("direct")} disabled={!canDirectTikTok} /><span><strong>바로 게시</strong><small>영상의 원본 오디오 유지</small></span></label>
                    <label><input type="radio" name={`tiktok-mode-${result.id}`} checked={tiktokMode === "draft"} onChange={() => setTikTokMode("draft")} disabled={!canDraftTikTok} /><span><strong>앱에서 음원 추가</strong><small>초안 전송 후 TikTok에서 선택</small></span></label>
                  </div>
                  {tiktokMode === "direct" && (
                    <>
                      <label>공개 범위<select value={privacy} onChange={(event) => setPrivacy(event.target.value)}><option value="">직접 선택</option>{creator?.privacy_level_options.map((option) => <option key={option} value={option}>{privacyLabel(option)}</option>)}</select></label>
                      <div className="social-check-row">
                        <label><input type="checkbox" checked={allowComment} onChange={(event) => setAllowComment(event.target.checked)} disabled={creator?.comment_disabled} />댓글</label>
                        <label><input type="checkbox" checked={allowDuet} onChange={(event) => setAllowDuet(event.target.checked)} disabled={creator?.duet_disabled} />Duet</label>
                        <label><input type="checkbox" checked={allowStitch} onChange={(event) => setAllowStitch(event.target.checked)} disabled={creator?.stitch_disabled} />Stitch</label>
                      </div>
                    </>
                  )}
                </fieldset>
              </div>

              <section className="social-cover-picker">
                <div><strong>커버 추천</strong><span>선명도·노출·중앙 피사체가 좋은 실제 영상 프레임입니다.</span></div>
                {coverLoading ? <p><LoaderCircle className="spin" size={15} />프레임 비교 중</p> : covers.length ? (
                  <div className="social-cover-options">
                    {covers.map((cover, index) => (
                      <button key={cover.timestampMs} className={cover.timestampMs === selectedCoverMs ? "selected" : ""} onClick={() => setSelectedCoverMs(cover.timestampMs)} aria-pressed={cover.timestampMs === selectedCoverMs}>
                        <img src={cover.previewUrl} alt={`${timeLabel(cover.timestampMs)} 커버 후보`} />
                        <span>{index === 0 ? "추천" : `후보 ${index + 1}`} · {timeLabel(cover.timestampMs)}</span>
                      </button>
                    ))}
                  </div>
                ) : <span className="social-cover-fallback">첫 프레임을 사용합니다.</span>}
              </section>

              {draft && (
                <section className="social-music-advice">
                  <Music2 size={17} />
                  <span><strong>{draft.music.mood || "원본 오디오"}</strong><small>{draft.music.reason}</small></span>
                  <div>{draft.music.search_terms.map((term) => <span key={term}>{term}</span>)}</div>
                  <p>직접 게시에서는 영상에 포함된 오디오를 유지합니다. 플랫폼 음원은 TikTok 초안 전송 후 앱에서 검색하세요.</p>
                </section>
              )}

              <div className="social-final-settings">
                {result.motion_fix.available && <label>게시 영상<select value={mediaVariant} onChange={(event) => setMediaVariant(event.target.value as "original" | "motion_fix")}><option value="original">원본 게시 마스터</option><option value="motion_fix">{result.motion_fix.factor}× 속도 보정본</option></select></label>}
                {tiktokEnabled && selectedTikTok && (
                  <>
                    <label className="social-commercial"><input type="checkbox" checked={commercial} onChange={(event) => { setCommercial(event.target.checked); if (!event.target.checked) { setOwnBrand(false); setBrandedContent(false); } }} />제품·브랜드를 홍보하는 콘텐츠</label>
                    {commercial && <div className="social-check-row commercial"><label><input type="checkbox" checked={ownBrand} onChange={(event) => setOwnBrand(event.target.checked)} />내 브랜드</label><label><input type="checkbox" checked={brandedContent} onChange={(event) => setBrandedContent(event.target.checked)} />협찬·타사 브랜드</label></div>}
                    {invalidCommercial && <span className="social-inline-error">홍보 유형을 하나 이상 선택하세요.</span>}
                    <label className="social-consent"><input type="checkbox" checked={musicConfirmed} onChange={(event) => setMusicConfirmed(event.target.checked)} /><span>TikTok Music Usage Confirmation에 동의하며, 게시할 영상과 문구를 확인했습니다.</span></label>
                  </>
                )}
                <p><Check size={14} />AI 생성 콘텐츠 표시는 두 플랫폼에 자동 전달됩니다.</p>
              </div>

              {error && <div className="social-error" role="alert"><AlertTriangle size={16} /><span>{error}</span></div>}

              <div className="social-publish-actions">
                <span>{selectedTargets ? `${selectedTargets}개 플랫폼 선택 · 커버 ${timeLabel(selectedCoverMs)}` : "게시 계정을 연결하고 선택하세요."}</span>
                <button className="button primary" onClick={() => void publish()} disabled={!canPublish}>
                  {publishing ? <LoaderCircle className="spin" size={17} /> : <Send size={17} />}
                  {publishing ? "전송 중" : selectedTargets > 1 ? "두 플랫폼에 게시" : "선택한 플랫폼에 게시"}
                </button>
              </div>

              <PublishHistory publishes={publishes} />
            </>
          )}
        </>
      )}
    </section>
  );
}

function SparkleMark() {
  return <span className="social-sparkle" aria-hidden="true">✦</span>;
}
